/**
 * Shrink camera photos before upload so they pass reverse-proxy body limits
 * (HSL nginx often defaults to ~1MB → 413 without CORS → axios "Network Error").
 */

/** Stay under typical nginx 1m limit (multipart overhead included). */
export const UPLOAD_PROXY_SAFE_MAX_BYTES = 800 * 1024;
/** Longest edge used when auto-shrinking uploads (px). */
export const UPLOAD_MAX_EDGE_PX = 1600;
/** Ideal product-photo longest edge before upload (px). */
export const IDEAL_PHOTO_EDGE_PX = 1600;
/** Ideal shortest edge so shop cards stay sharp (px). */
export const IDEAL_PHOTO_MIN_EDGE_PX = 800;
/** Preferred aspect for set hero / card photos. */
export const IDEAL_PHOTO_ASPECT = '4:3 or 1:1';
/** Preferred file formats shown to users. */
export const IDEAL_PHOTO_FORMATS = 'JPG or PNG';
/** Soft size target before auto-shrink (MB). */
export const IDEAL_PHOTO_MAX_MB = 2;

const DEFAULT_MAX_EDGE = UPLOAD_MAX_EDGE_PX;
const MIN_EDGE = 480;

/** Short guidance shown next to Manage Photos / wizard upload controls. */
export function getSetPhotoUploadGuidance(): string {
  return (
    `Ideal photo: ${IDEAL_PHOTO_EDGE_PX}×${Math.round((IDEAL_PHOTO_EDGE_PX * 3) / 4)}px ` +
    `(or square ${IDEAL_PHOTO_EDGE_PX}×${IDEAL_PHOTO_EDGE_PX}), ${IDEAL_PHOTO_ASPECT}, ` +
    `${IDEAL_PHOTO_FORMATS}, under ~${IDEAL_PHOTO_MAX_MB}MB. ` +
    `Larger camera photos are shrunk automatically (max edge ${UPLOAD_MAX_EDGE_PX}px, ~${Math.round(UPLOAD_PROXY_SAFE_MAX_BYTES / 1024)}KB).`
  );
}

const IMAGE_EXT = /\.(jpe?g|png|webp|bmp|heic|heif)$/i;

export class ImageTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageTooLargeError';
  }
}

function looksLikeImage(file: File): boolean {
  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('image/') && type !== 'image/gif' && type !== 'image/svg+xml') {
    return true;
  }
  // Some cameras/OS pickers omit MIME — trust the extension.
  return IMAGE_EXT.test(file.name || '');
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    // Prefer data: over blob: — production CSP (Helmet/nginx) often blocks blob: images.
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Could not read image for compression'));
        return;
      }
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not read image for compression'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Could not read image for compression'));
    reader.readAsDataURL(file);
  });
}

async function drawToCanvas(
  source: CanvasImageSource,
  width: number,
  height: number
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable for image compression');
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

function scaledSize(
  srcW: number,
  srcH: number,
  maxEdge: number
): { width: number; height: number } {
  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH, 1));
  return {
    width: Math.max(1, Math.round(srcW * scale)),
    height: Math.max(1, Math.round(srcH * scale)),
  };
}

/**
 * Return a JPEG File sized for upload.
 * - Small images under the limit are returned unchanged.
 * - Large photos are resized/compressed automatically.
 * - Throws ImageTooLargeError if the result still cannot fit (rare / undecodable).
 */
export async function compressImageForUpload(
  file: File,
  options?: { maxBytes?: number; maxEdge?: number }
): Promise<File> {
  if (!looksLikeImage(file)) return file;

  const maxBytes = options?.maxBytes ?? UPLOAD_PROXY_SAFE_MAX_BYTES;
  const maxEdge = options?.maxEdge ?? DEFAULT_MAX_EDGE;

  // Already under the proxy-safe size — keep original bytes.
  if (file.size <= maxBytes) return file;

  if (typeof document === 'undefined') {
    throw new ImageTooLargeError(
      `Photo is about ${(file.size / (1024 * 1024)).toFixed(1)}MB and cannot be shrunk in this environment (limit ~${Math.round(maxBytes / 1024)}KB).`
    );
  }

  try {
    let width = 0;
    let height = 0;
    let source: CanvasImageSource | null = null;
    let bitmap: ImageBitmap | null = null;

    // Prefer createImageBitmap — handles large camera JPEGs more reliably than <img>.
    if (typeof createImageBitmap === 'function') {
      try {
        const probe = await createImageBitmap(file);
        ({ width, height } = scaledSize(probe.width, probe.height, maxEdge));
        probe.close();
        bitmap = await createImageBitmap(file, {
          resizeWidth: width,
          resizeHeight: height,
          resizeQuality: 'high',
        } as ImageBitmapOptions);
        source = bitmap;
      } catch {
        bitmap = null;
        source = null;
      }
    }

    if (!source) {
      const img = await loadImageElement(file);
      ({ width, height } = scaledSize(img.width, img.height, maxEdge));
      source = img;
    }

    let canvas = await drawToCanvas(source, width, height);
    if (bitmap) {
      try {
        bitmap.close();
      } catch {
        /* ignore */
      }
    }

    // Always JPEG for set photos — much smaller than PNG/WebP from some phones.
    let quality = 0.82;
    let blob = await canvasToJpegBlob(canvas, quality);

    while (blob && blob.size > maxBytes && quality > 0.4) {
      quality = Math.max(0.4, quality - 0.12);
      blob = await canvasToJpegBlob(canvas, quality);
    }

    while (blob && blob.size > maxBytes && Math.max(canvas.width, canvas.height) > MIN_EDGE) {
      const nextW = Math.max(MIN_EDGE, Math.round(canvas.width * 0.7));
      const nextH = Math.max(
        1,
        Math.round(canvas.height * (nextW / Math.max(canvas.width, 1)))
      );
      // Re-draw from the current canvas (already downscaled) to avoid keeping the huge original in memory.
      canvas = await drawToCanvas(canvas, nextW, nextH);
      blob = await canvasToJpegBlob(canvas, Math.max(quality, 0.55));
    }

    if (!blob) {
      throw new ImageTooLargeError(
        'Could not compress this photo. Try another JPG/PNG under 10MB.'
      );
    }

    if (blob.size > maxBytes) {
      throw new ImageTooLargeError(
        `Photo is still too large after shrinking (${Math.round(blob.size / 1024)}KB; limit ~${Math.round(maxBytes / 1024)}KB). Try a different photo.`
      );
    }

    const base = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${base}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (err) {
    if (err instanceof ImageTooLargeError) throw err;
    // Undecodable (e.g. HEIC in some browsers) — do not send the huge original.
    throw new ImageTooLargeError(
      `Could not shrink this photo before upload (${(file.size / (1024 * 1024)).toFixed(1)}MB). Use a JPG/PNG, or take a smaller photo.`
    );
  }
}
